import { LangType } from '../../helpers/lang';
import { buildParams } from '../../helpers/path';
import { PaginationRequest, PaginationType } from '../../types/pagination';
import {
  CustomCacheRequest,
  DefaultRequest,
  IdValue,
  InfiniteScrollRequest,
  SortRequest,
  WithArchiveRequest,
} from '../../types/props';
import { ImageType } from './image-api';
import { managerApiUrl } from './manager-api';
import { RoleType } from './role-api';
import { baseFetch } from './utils.ts/base-fetch';

const baseUrl = `${managerApiUrl}/staff`;

export type EmployeeType = {
  created_at: string;
  places: {
    id: number;
    value: LangType;
  }[];
  id: number;
  is_active?: boolean;
  is_joining?: boolean;
  name: LangType;
  surname: LangType;
  patronymic: LangType;
  tags: IdValue[];
  online: boolean;
  shift_state: 'ON_SHIFT' | 'NONE';
  is_deleted?: boolean;
  profile?: Partial<{
    display_title: LangType;
    description?: LangType;
    phone: string;
    email?: string;
    avatar: ImageType;
    available_qrs: { id: string; value: LangType }[];
  }>;
  roles?: RoleType[];
};

export type FetchEmployeesRequest = {
  name?: string;
  tags?: string;
  from?: string;
  to?: string;
  online?: string;
  is_available?: boolean;
  shift_state?: 'ON_SHIFT' | 'NONE';
  place_ids?: string;
} & PaginationRequest &
  CustomCacheRequest &
  SortRequest &
  WithArchiveRequest &
  DefaultRequest &
  InfiniteScrollRequest;

export const fetchEmployees = async (
  params: FetchEmployeesRequest,
): Promise<{ staff: EmployeeType[] } & PaginationType> => {
  const { customCache, ...other } = params;

  const response = await baseFetch(`${managerApiUrl}/staff${buildParams(other)}`, {
    customCache,
  });
  return response;
};

export const fetchEmployeeById = async (id: string): Promise<EmployeeType> => {
  const response = await baseFetch(`${managerApiUrl}/staff/${id}`);
  return response;
};

export const postEmployees = async (data: Partial<EmployeeType>): Promise<EmployeeType> => {
  const withBadTags = { ...data, tags: data.tags?.map((item) => item.id) };
  const response = await baseFetch(baseUrl, {
    method: 'POST',
    body: JSON.stringify(withBadTags),
  });
  return response;
};

export const putEmployees = async (data: Partial<EmployeeType>): Promise<EmployeeType> => {
  const withBadTags = { ...data, tags: data.tags?.map((item) => item.id) };
  const response = await baseFetch(`${baseUrl}/${data.id}`, {
    method: 'PUT',
    body: JSON.stringify(withBadTags),
  });
  return response;
};

export const deleteEmployees = async (id: string): Promise<void> => {
  await baseFetch(`${baseUrl}/${id}`, {
    method: 'DELETE',
  });
};

export const restoreEmployee = async (id: string): Promise<void> => {
  const response = await baseFetch(baseUrl + `/${id}/restore`, { method: 'POST' });
  return response;
};

export const unlinkEmployees = async (id: string): Promise<void> => {
  const response = await baseFetch(`${baseUrl}/${id}/logout`, {
    method: 'POST',
  });
  return response;
};
